import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import SignupPage from "@/pages/SignupPage";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import PortalLandingPage from "@/pages/PortalLandingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordConfirmPage from "@/pages/ResetPasswordConfirmPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import AdminHomePage from "@/pages/admin/AdminHomePage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminMetricsPage from "@/pages/admin/AdminMetricsPage";
import AdminResetPasswordConfirmPage from "@/pages/admin/AdminResetPasswordConfirmPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import { GLOFOX_PORTAL_DOCS } from "@/content/glofox/portal-docs/manifest";
import {
  useFeatureFlagsResolved,
  useMultiPlatformDocsEnabled,
  useSandboxCredentialsEnabled,
} from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import DocsFramePage from "@/pages/glofox/DocsFramePage";
import ApiReferencePage from "@/pages/glofox/ApiReferencePage";
import { GlofoxPortalDocRoute } from "@/pages/glofox/GlofoxPortalDocPage";
import RequestAccessPage from "@/pages/glofox/RequestAccessPage";
import { GLOFOX_DOCS_HOME_ROUTE } from "@/lib/portal/docs-urls";
import {
  GLOFOX_REQUEST_ACCESS_DOCS_ROUTE,
  GLOFOX_REQUEST_ACCESS_HREF,
} from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LANDING_ROUTE,
  PORTAL_LOGIN_LEGACY_ROUTE,
  PORTAL_SIGNUP_LEGACY_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";

/** Preserve search/state when rewriting legacy auth paths. */
function RedirectPreserve({ to }: { to: string }) {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      state={location.state}
      replace
    />
  );
}

function FlaggedLegacyDocRoute({ route }: { route: string }) {
  const slice6Enabled = useSandboxCredentialsEnabled();
  return slice6Enabled ? (
    <DocsFramePage />
  ) : (
    <GlofoxPortalDocRoute route={route} />
  );
}

function FlaggedApiReferenceRoute() {
  return useSandboxCredentialsEnabled() ? <DocsFramePage /> : <ApiReferencePage />;
}

function FlaggedRequestAccessRoute() {
  return useSandboxCredentialsEnabled() ? <DocsFramePage /> : <RequestAccessPage />;
}

function FlaggedSlice6RequestAccessRoute() {
  return useSandboxCredentialsEnabled() ? (
    <RequestAccessPage />
  ) : (
    <RedirectPreserve to={GLOFOX_REQUEST_ACCESS_DOCS_ROUTE} />
  );
}

function FlaggedLoginRoute() {
  return useSandboxCredentialsEnabled() ? (
    <RedirectPreserve to={PORTAL_HOME_ROUTE} />
  ) : (
    <LoginPage />
  );
}

function FlaggedSignupRoute() {
  return useSandboxCredentialsEnabled() ? (
    <RedirectPreserve to={PORTAL_SIGNUP_ROUTE} />
  ) : (
    <SignupPage />
  );
}

function FlaggedLegacyDocsFallback() {
  return useSandboxCredentialsEnabled() ? (
    <DocsFramePage />
  ) : (
    <Navigate to={GLOFOX_DOCS_HOME_ROUTE} replace />
  );
}

/** Keeps one DocsFramePage mounted across `/glofox` and `/glofox/*` child matches. */
function GlofoxSlice6DocsLayout() {
  return (
    <>
      <DocsFramePage />
      <Outlet />
    </>
  );
}

function FlaggedRootRoute() {
  return useMultiPlatformDocsEnabled() ? (
    <Navigate to={PORTAL_LANDING_ROUTE} replace />
  ) : (
    <Navigate to={GLOFOX_DOCS_HOME_ROUTE} replace />
  );
}

function FlaggedPortalLandingRoute() {
  return useMultiPlatformDocsEnabled() ? (
    <PortalLandingPage />
  ) : (
    <Navigate to={GLOFOX_DOCS_HOME_ROUTE} replace />
  );
}

export function AppRoutes() {
  const flagsResolved = useFeatureFlagsResolved();
  const slice6Enabled = useSandboxCredentialsEnabled();

  // Routing decisions depend on the flag, and several of them replace the
  // current entry. Rendering them against the fail-closed defaults would strand
  // the user on the legacy destination once the real evaluation arrives.
  if (!flagsResolved) {
    return (
      <p className="sr-only" role="status">
        Loading
      </p>
    );
  }

  const glofoxLegacyDocRoutes = GLOFOX_PORTAL_DOCS.map((doc) => (
    <Route
      key={doc.id}
      path={doc.route}
      element={<FlaggedLegacyDocRoute route={doc.route} />}
    />
  ));

  const glofoxSlice6DocRoutes = (
    <Route element={<GlofoxSlice6DocsLayout />}>
      <Route path="/glofox" />
      <Route path="/glofox/*" />
    </Route>
  );

  return (
    <Routes>
      <Route path="/" element={<FlaggedRootRoute />} />
      <Route
        path={PORTAL_LANDING_ROUTE}
        element={<FlaggedPortalLandingRoute />}
      />
      <Route path={PORTAL_HOME_ROUTE} element={<HomePage />} />
      <Route path={PORTAL_SIGNUP_ROUTE} element={<SignupPage />} />
      <Route
        path={PORTAL_LOGIN_LEGACY_ROUTE}
        element={<FlaggedLoginRoute />}
      />
      <Route
        path={PORTAL_SIGNUP_LEGACY_ROUTE}
        element={<FlaggedSignupRoute />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route
        path="/forgot-password/confirm"
        element={<ResetPasswordConfirmPage />}
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin/forgot-password"
        element={<AdminForgotPasswordPage />}
      />
      <Route
        path="/admin/forgot-password/confirm"
        element={<AdminResetPasswordConfirmPage />}
      />
      <Route path="/admin/control-panel" element={<AdminHomePage />} />
      <Route path="/admin/metrics" element={<AdminMetricsPage />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route
        path="/admin"
        element={<Navigate to="/admin/control-panel" replace />}
      />
      <Route
        path="/admin/drawer"
        element={<Navigate to="/admin/control-panel" replace />}
      />
      <Route
        path={GLOFOX_REQUEST_ACCESS_HREF}
        element={<FlaggedSlice6RequestAccessRoute />}
      />
      {slice6Enabled ? (
        <>
          <Route
            path="/glofox/login"
            element={<FlaggedLoginRoute />}
          />
          <Route
            path="/glofox/signup"
            element={<FlaggedSignupRoute />}
          />
          <Route
            path="/glofox/sandbox"
            element={<Navigate to={PORTAL_HOME_ROUTE} replace />}
          />
          {glofoxSlice6DocRoutes}
        </>
      ) : (
        <>
          <Route
            path={GLOFOX_REQUEST_ACCESS_DOCS_ROUTE}
            element={<FlaggedRequestAccessRoute />}
          />
          {glofoxLegacyDocRoutes}
          <Route
            path="/glofox/api-reference"
            element={<FlaggedApiReferenceRoute />}
          />
          <Route
            path="/glofox/sandbox"
            element={<Navigate to={PORTAL_HOME_ROUTE} replace />}
          />
          <Route
            path="/glofox/signup"
            element={<FlaggedSignupRoute />}
          />
          <Route
            path="/glofox/login"
            element={<FlaggedLoginRoute />}
          />
          <Route path="/glofox/*" element={<FlaggedLegacyDocsFallback />} />
        </>
      )}
    </Routes>
  );
}
